/**
 * Helpers purs pour le refresh proactif du token côté middleware, seul contexte
 * capable de réécrire le cookie next-auth (impossible dans le callback jwt RSC).
 */

export interface RefreshableToken {
  dynabuyExpiry?: number;
  dynabuyRefreshToken?: string;
}

/**
 * Vrai si un refresh token existe ET que l'expiry est passée ou dans la fenêtre
 * `thresholdMs` à venir.
 */
export function shouldRefresh(
  token: RefreshableToken | null | undefined,
  now: number,
  thresholdMs: number,
): boolean {
  if (!token?.dynabuyRefreshToken || !token.dynabuyExpiry) return false;
  return token.dynabuyExpiry - now <= thresholdMs;
}

/**
 * Nom du cookie de session next-auth selon l'URL (secure en https). Doit matcher
 * exactement celui écrit au login, sinon on crée un second cookie fantôme.
 */
export function getSessionCookieName(nextauthUrl: string | undefined): string {
  const useSecure = (nextauthUrl ?? "").startsWith("https://");
  return useSecure
    ? "__Secure-next-auth.session-token"
    : "next-auth.session-token";
}

/**
 * Hash court non réversible sans node:crypto (djb2), pour le runtime middleware.
 * Sert à corréler une session entre lignes de log — jamais à sécuriser.
 */
export function shortHashPure(value?: string | null): string {
  if (!value) return "none";
  let hash = 5381;
  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) + hash + value.charCodeAt(i)) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

/**
 * Remplace (ou ajoute) un cookie dans un en-tête `Cookie`, en préservant les
 * autres. Propage le token rafraîchi au rendu de la même requête.
 */
export function setCookieInHeader(
  cookieHeader: string | null | undefined,
  name: string,
  value: string,
): string {
  const pairs = (cookieHeader ?? "")
    .split(";")
    .map((p) => p.trim())
    .filter(Boolean);

  let replaced = false;
  const next = pairs.map((pair) => {
    if (pair.slice(0, pair.indexOf("=")) === name) {
      replaced = true;
      return `${name}=${value}`;
    }
    return pair;
  });

  if (!replaced) next.push(`${name}=${value}`);
  return next.join("; ");
}

export interface RefreshedTokens {
  token: string;
  refresh_token?: string;
}

/** Erreur de refresh portant le status HTTP, pour arbitrer logout vs fail-open. */
export class RefreshError extends Error {
  status: number;
  constructor(status: number, message?: string) {
    super(message ?? `refresh failed (${status})`);
    this.name = "RefreshError";
    this.status = status;
  }
}

/**
 * Appelle l'API de refresh. Renvoie les nouveaux tokens, ou throw un RefreshError
 * si la réponse n'est pas OK (401/403 → logout côté middleware). Une erreur réseau
 * laisse fetch throw (TypeError), traitée en fail-open (transitoire).
 */
export async function refreshAccessToken(
  backAPIUrl: string,
  refreshTokenValue: string | undefined,
): Promise<RefreshedTokens> {
  const response = await fetch(`${backAPIUrl}/auth/token/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshTokenValue }),
    cache: "no-store",
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    const rawBody = await response.text();

    let message: string | undefined;
    if (contentType.includes("application/json")) {
      try {
        message = (JSON.parse(rawBody) as { message?: string })?.message;
      } catch {
        /* JSON annoncé mais invalide : message par défaut */
      }
    }
    throw new RefreshError(response.status, message);
  }

  return (await response.json()) as RefreshedTokens;
}

export interface RevocableToken {
  dynabuyToken?: string;
  dynabuyRefreshToken?: string;
}

export async function revokeTokens(
  backAPIUrl: string,
  token: RevocableToken,
): Promise<void> {
  if (!token.dynabuyRefreshToken && !token.dynabuyToken) return;

  const logout = (accessToken?: string) =>
    fetch(`${backAPIUrl}/auth/logout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({ refresh_token: token.dynabuyRefreshToken }),
      cache: "no-store",
    });

  const response = await logout(token.dynabuyToken);
  if (response.status === 401 && token.dynabuyToken) {
    await logout();
  }
}
